import React, { useState, useMemo } from 'react';
import StackedLaborChart, { clipSeriesToRange, clipCategoriesToRange } from './StackedLaborChart';
import SegmentedControl from './SegmentedControl';

/**
 * Available Labor section: per-cohort stacked area chart with a "Plan window
 * / Full timeline" toggle that clips the series to the planning interval span.
 */
const AvailableLaborChart = ({ laborAvailabilityChart, intervals }) => {
  const { perCohortAvailable, totalAvailable, netAvailable, categories } = laborAvailabilityChart || {};
  const [isAvailableExpanded, setIsAvailableExpanded] = useState(false);

  const planStartLabel = intervals?.[0]?.startLabel;
  const planEndLabel = intervals?.[intervals.length - 1]?.endLabel;
  const canTruncate = !!(planStartLabel && planEndLabel && categories?.length);

  // Per-cohort sweep-line series.
  const baseSeries = useMemo(
    () => (perCohortAvailable || []).map((c, i) => ({ key: `cohort-${i}`, label: c.label, data: c.data })),
    [perCohortAvailable]
  );

  if (!baseSeries.length) return null;

  const showFullTimeline = isAvailableExpanded || !canTruncate;

  const displayedSeries = showFullTimeline
    ? baseSeries
    : baseSeries.map((s) => ({
        ...s,
        data: clipSeriesToRange(s.data, categories, planStartLabel, planEndLabel)
      }));
  const displayedTotal = showFullTimeline
    ? totalAvailable || []
    : clipSeriesToRange(totalAvailable, categories, planStartLabel, planEndLabel);
  const displayedNet = showFullTimeline
    ? netAvailable || []
    : clipSeriesToRange(netAvailable, categories, planStartLabel, planEndLabel);
  const displayedCategories = showFullTimeline
    ? categories || []
    : clipCategoriesToRange(categories, planStartLabel, planEndLabel);

  return (
    <StackedLaborChart
      title="Available Labor"
      series={displayedSeries}
      totalData={displayedTotal}
      netData={displayedNet}
      intervals={intervals}
      categories={displayedCategories}
      headerExtras={
        canTruncate && (
          <SegmentedControl
            value={isAvailableExpanded ? 'full' : 'plan'}
            options={[
              { value: 'plan', label: 'Plan window' },
              { value: 'full', label: 'Full timeline' }
            ]}
            onChange={(v) => setIsAvailableExpanded(v === 'full')}
            ariaLabel="Show plan window or full timeline"
          />
        )
      }
    />
  );
};

export default AvailableLaborChart;
